import { useMemo, useState, useEffect, useCallback } from 'react'

import { SIZE_CLASS_MAP } from './snowflake-area.consts'
import type { SnowflakeAreaModelProps, SnowflakeItem } from './snowflake-area.types'

export const useSnowflakeArea = ({
  smallCount,
  mediumCount,
  largeCount,
  autoHideDuration,
  onAutoHideComplete,
}: SnowflakeAreaModelProps) => {
  const [isVisible, setIsVisible] = useState(true)
  const [isFadingOut, setIsFadingOut] = useState(false)

  const snowflakes = useMemo((): SnowflakeItem[] => {
    const items: SnowflakeItem[] = []

    // Generate small snowflakes
    for (let i = 1; i <= smallCount; i++) {
      items.push({
        id: `small-${i}`,
        size: 'small',
        index: i,
      })
    }

    // Generate medium snowflakes
    for (let i = 1; i <= mediumCount; i++) {
      items.push({
        id: `medium-${i}`,
        size: 'medium',
        index: i,
      })
    }

    // Generate large snowflakes
    for (let i = 1; i <= largeCount; i++) {
      items.push({
        id: `large-${i}`,
        size: 'large',
        index: i,
      })
    }

    return items
  }, [smallCount, mediumCount, largeCount])

  const handleAutoHideComplete = useCallback(() => {
    setIsVisible(false)
    onAutoHideComplete?.()
  }, [onAutoHideComplete])

  useEffect(() => {
    if (!autoHideDuration) return

    const timer = setTimeout(() => {
      setIsFadingOut(true)
    }, autoHideDuration)

    return () => clearTimeout(timer)
  }, [autoHideDuration])

  const getSnowflakeClassName = useCallback((snowflake: SnowflakeItem): string => {
    const mapper = SIZE_CLASS_MAP[snowflake.size]
    return typeof mapper === 'function' ? mapper(snowflake.index) : 'snowflake'
  }, [])

  return {
    snowflakes,
    getSnowflakeClassName,
    isVisible,
    isFadingOut,
    onAutoHideComplete: handleAutoHideComplete,
  }
}
